import { useId, useState, type ReactNode } from 'react';
import { presetOf, presetRange, type DatePreset, type DateRange } from '@/domain';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { usePhoneLayout } from '../layout/usePhoneLayout';
import { filterInUse } from './filterInUse';
import styles from './FilterBar.module.css';

/**
 * One control in a filter bar.
 *
 * The bar owns how a filter looks and behaves; the screen owns what it means and
 * where the filtering happens. Orders, a stage and a person's record filter in
 * memory, because they hold tens of rows already fetched; the archive filters on
 * the server, because it holds every case the lab has ever delivered. Either way
 * the bar is the same bar.
 */
export type FilterDef =
  | {
      kind: 'search';
      label: string;
      value: string;
      onChange: (next: string) => void;
      placeholder: string;
    }
  | {
      kind: 'select';
      label: string;
      value: string;
      options: { value: string; label: string }[];
      onChange: (next: string) => void;
    }
  | {
      kind: 'dateRange';
      label: string;
      from: string;
      to: string;
      onChange: (range: DateRange) => void;
    }
  | { kind: 'toggle'; label: string; value: boolean; onChange: (next: boolean) => void };

export interface FilterBarProps {
  /** Names the bar for a screen reader, since a screen may carry a second one on a tab. */
  label: string;
  filters: FilterDef[];
  /** The order control, `SortSelect`. Narrows nothing, so it sits after everything that does. */
  sort?: ReactNode;
  /** Acts on the filtered set, as the archive's export does. Sits at the end of the bar. */
  actions?: ReactNode;
  /**
   * Whether the bar folds away behind one button on a phone, which it does
   * unless the screen has a phone layout of its own: a doctor's cases keep the
   * search on the page and the rest in a sheet, and that sheet is a bar too.
   */
  foldOnPhone?: boolean;
}

const PRESET_LABEL: Record<DatePreset, string> = {
  all: strings.filters.allTime,
  today: strings.filters.today,
  week: strings.filters.thisWeek,
  month: strings.filters.thisMonth,
  custom: strings.filters.customRange,
};

/**
 * The filter bar over every case table.
 *
 * Every control is a touch target tall, and the bar wraps to one control per
 * line below 620px, the archive's own breakpoint, since that is where these
 * rules were first written and tested.
 *
 * On a phone it folds away behind one button, closed when the screen opens,
 * which says how many filters are narrowing the list while it is closed. The
 * lab's request of 17 October 2026, item 2: the table is what a phone opens a
 * list for, and five controls stacked one per line pushed it a screen down.
 */
export function FilterBar({ label, filters, sort, actions, foldOnPhone = true }: FilterBarProps) {
  const phone = usePhoneLayout() && foldOnPhone;
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const inUse = filters.filter(filterInUse).length;

  const controls = (
    <>
      {filters.map((filter) => (
        <Filter key={`${filter.kind}:${filter.label}`} filter={filter} />
      ))}
      {/* The order, at the end of the bar: it narrows nothing, so it comes
          after everything that does. Section 3 of the request of
          13 September 2026. */}
      {sort !== undefined && <div className={styles.field}>{sort}</div>}
      {actions !== undefined && <div className={styles.field}>{actions}</div>}
    </>
  );

  if (!phone) {
    return (
      <div className={styles.filters} role="search" aria-label={label}>
        {controls}
      </div>
    );
  }

  return (
    <div className={`${styles.filters} ${styles.folding}`} role="search" aria-label={label}>
      <button
        type="button"
        className={styles.fold}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((was) => !was)}
      >
        <span>{strings.filters.searchAndFilters}</span>
        {inUse > 0 && <span className={styles.inUse}>{strings.filters.inUse(inUse)}</span>}
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true" />
      </button>
      <div id={bodyId} className={styles.body} hidden={!open}>
        {controls}
      </div>
    </div>
  );
}

function Filter({ filter }: { filter: FilterDef }) {
  const id = useId();

  switch (filter.kind) {
    case 'search':
      return (
        <div className={`${styles.field} ${styles.grow}`}>
          <label className={styles.l} htmlFor={id}>
            {filter.label}
          </label>
          <input
            id={id}
            className={styles.input}
            value={filter.value}
            placeholder={filter.placeholder}
            onChange={(event) => filter.onChange(event.target.value)}
          />
        </div>
      );

    case 'select':
      return (
        <div className={styles.field}>
          <label className={styles.l} htmlFor={id}>
            {filter.label}
          </label>
          <select
            id={id}
            className={styles.select}
            value={filter.value}
            onChange={(event) => filter.onChange(event.target.value)}
          >
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'toggle':
      return (
        <div className={styles.field}>
          <label className={`${styles.toggle} ${filter.value ? styles.on : ''}`}>
            <input
              type="checkbox"
              checked={filter.value}
              onChange={(event) => filter.onChange(event.target.checked)}
            />
            {filter.label}
          </label>
        </div>
      );

    case 'dateRange':
      return <DateRangeFilter id={id} filter={filter} />;
  }
}

/**
 * A date range, offered as the archive's presets with a custom range behind the
 * last of them.
 *
 * The preset is remembered here rather than worked out from the range on every
 * render: somebody who picks Custom and has not yet typed a date has chosen
 * Custom, even though the range still reads as All time.
 */
function DateRangeFilter({
  id,
  filter,
}: {
  id: string;
  filter: Extract<FilterDef, { kind: 'dateRange' }>;
}) {
  // The lab's day, not the device's: a filter opened at two in the morning in
  // Dubai starts today, not yesterday in UTC.
  const today = formatDay(new Date().toISOString());
  const [preset, setPreset] = useState<DatePreset>(() =>
    presetOf({ from: filter.from, to: filter.to }, today),
  );

  return (
    <>
      <div className={styles.field}>
        <label className={styles.l} htmlFor={id}>
          {filter.label}
        </label>
        <select
          id={id}
          className={styles.select}
          value={preset}
          onChange={(event) => {
            const next = event.target.value as DatePreset;
            setPreset(next);
            if (next !== 'custom') filter.onChange(presetRange(next, today));
          }}
        >
          {(Object.keys(PRESET_LABEL) as DatePreset[]).map((key) => (
            <option key={key} value={key}>
              {PRESET_LABEL[key]}
            </option>
          ))}
        </select>
      </div>

      {preset === 'custom' && (
        <div className={styles.field}>
          <span className={styles.l}>
            {strings.filters.from} / {strings.filters.to}
          </span>
          <div className={styles.dates}>
            <input
              type="date"
              className={styles.input}
              aria-label={strings.filters.from}
              value={filter.from}
              onChange={(event) => filter.onChange({ from: event.target.value, to: filter.to })}
            />
            <input
              type="date"
              className={styles.input}
              aria-label={strings.filters.to}
              value={filter.to}
              onChange={(event) => filter.onChange({ from: filter.from, to: event.target.value })}
            />
          </div>
        </div>
      )}
    </>
  );
}
